import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DesktopPostCarousel from './DesktopPostCarousel';
import MobileSignalIcon from '../../../../assets/images/icons/Instagram_icon/Mobile-Signal.svg';
import BatteryIcon from '../../../../assets/images/icons/Instagram_icon/Battery.svg';
import WifiIcon from '../../../../assets/images/icons/Instagram_icon/Wifi.svg';
import LikeIcon from '../../../../assets/images/icons/Instagram_icon/Like.svg';
import MessengerIcon from '../../../../assets/images/icons/Instagram_icon/Messanger.svg';
import SaveIcon from '../../../../assets/images/icons/Instagram_icon/Save.svg';
import CommentIcon from '../../../../assets/images/icons/Instagram_icon/Comment.svg';
import AddIcon from '../../../../assets/images/icons/Instagram_icon/add-icon.svg';
import HomeIcon from '../../../../assets/images/icons/Instagram_icon/home-icon.svg';
import SearchIcon from '../../../../assets/images/icons/Instagram_icon/search-icon.svg';
import AddStoryIcon from '../../../../assets/images/icons/Instagram_icon/icon-add-story.svg';
import CameraIcon from '../../../../assets/images/icons/Instagram_icon/Camera-Icon.svg';
import iconDownload from '../../../../assets/images/icons/icon-download.svg';
import iconClose from '../../../../assets/images/icons/icon-close.svg';
import MockStory1 from '../../../../assets/images/mock-img-insta-1.png';
import MockStory2 from '../../../../assets/images/mock-img-insta-2.png';
import MockStory3 from '../../../../assets/images/mock-img-insta-3.png';
import MockStory4 from '../../../../assets/images/mock-img-insta-4.png';
import useLanguage from '../../../../hooks/useLanguage';
import { Skeleton } from '../../../effects';
import { API_CONFIG } from '../../../../config/api';
import { normalizeMediaUrl } from '../../../../utils/normalizeMediaUrl';

const LONG_PRESS_DURATION_MS = 550;
const LONG_PRESS_MOVE_THRESHOLD = 8;

const isVideoFile = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
  const lower = url.toLowerCase();
  return videoExtensions.some((ext) => lower.includes(ext));
};

const Iphone16Pro = forwardRef(({
  width = 433,
  height = 882,
  deviceTime = '18:36',
  profileName,
  profileLocation,
  relativeTimeLabel,
  avatarUrl,
  mediaUrls = [],
  currentImageIndex = 0,
  onImageIndexChange,
  onRegenerateImage,
  onUploadImage,
  isRegenerating = false,
  isProcessing = false,
  isCurrentPostPublished = false,
  getImageLoadingProps,
  likeCount = 183,
  captionText,
  hashtagsLine = '',
  commentsHint,
  className,
  shadow = true,
  hoverAnimation = true,
  screenRadius = 55,
  isSkeleton = false,
  ...props
}, ref) => {
  const { t, language } = useLanguage();
  const baseWidth = 1179;
  const baseHeight = 2556;
  const bezelInset = 7;
  const screenInset = bezelInset + 1;
  const normalizedMedia = Array.isArray(mediaUrls) ? mediaUrls : [];

  const screenWidth = width - screenInset * 2;
  const screenHeight = height - screenInset * 2;
  const contentScale = Math.min(screenWidth / baseWidth, screenHeight / baseHeight);
  const contentScaleX = screenWidth / baseWidth;
  const contentScaleY = screenHeight / baseHeight;

  const placeholderStories = useMemo(() => ([
    { name: 'giulia.rossi', avatar: MockStory1 },
    { name: 'marco.bianchi', avatar: MockStory2 },
    { name: 'ilaria.fontana', avatar: MockStory3 },
    { name: 'andrea.neri', avatar: MockStory4 }
  ]), []);

  const counts = useMemo(() => {
    const safeLike = Number.isFinite(likeCount) ? likeCount : 0;
    return {
      likes: Math.max(0, safeLike),
      comments: Math.max(12, Math.round(safeLike / 4)),
      shares: Math.max(8, Math.round(safeLike / 6))
    };
  }, [likeCount]);

  const resolvedProfileName = profileName || t('postReview.preview.defaults.profileName', { defaultValue: 'nome.profilo.personale' });
  const resolvedProfileLocation = profileLocation || t('postReview.preview.defaults.profileLocation', { defaultValue: 'Luogo' });
  const resolvedRelativeTimeLabel = relativeTimeLabel || t('postReview.preview.defaults.relativeTimeLabel', { defaultValue: '2 dicembre' });
  const resolvedCaptionText = captionText || t('postReview.preview.defaults.captionText', { defaultValue: 'Descrizione post' });
  const resolvedCommentsHint = commentsHint || t('postReview.preview.defaults.commentsHint', { defaultValue: 'Visualizza tutti i commenti' });

  const combinedClassName = [
    'loopino-iphone16-pro',
    shadow ? 'loopino-iphone16-pro-shadow' : '',
    hoverAnimation ? 'loopino-iphone16-pro-hover' : '',
    'loopino-iphone-shell',
    className || ''
  ].filter(Boolean).join(' ');

  const handleIndexChange = (nextIndex) => {
    if (typeof onImageIndexChange === 'function') {
      onImageIndexChange(nextIndex);
    }
  };

  const [viewerUrl, setViewerUrl] = useState(null);
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressStartRef = useRef({ x: 0, y: 0 });
  const longPressTargetRef = useRef(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressTargetRef.current = null;
  }, []);

  const openViewer = useCallback((url) => {
    if (!url) return;
    setViewerUrl(url);
    setIsViewerVisible(true);
  }, []);

  const closeViewer = useCallback(() => {
    setIsViewerVisible(false);
    setTimeout(() => setViewerUrl(null), 180);
  }, []);

  const handleViewerDownload = useCallback(() => {
    if (!viewerUrl) return;
    let filename = 'download';
    try {
      const url = new URL(viewerUrl, window.location.origin);
      filename = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || 'download');
    } catch (error) {
      filename = 'download';
    }

    const link = document.createElement('a');
    link.href = viewerUrl;
    link.download = filename;
    link.rel = 'noopener';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [viewerUrl]);

  const handleLongPressStart = useCallback((clientX, clientY) => {
    const currentUrl = normalizedMedia[currentImageIndex];
    if (!currentUrl || isVideoFile(currentUrl)) {
      return;
    }
    longPressStartRef.current = { x: clientX, y: clientY };
    longPressTargetRef.current = currentUrl;
    longPressTimerRef.current = window.setTimeout(() => {
      openViewer(currentUrl);
      clearLongPressTimer();
    }, LONG_PRESS_DURATION_MS);
  }, [clearLongPressTimer, currentImageIndex, normalizedMedia, openViewer]);

  const handleLongPressMove = useCallback((clientX, clientY) => {
    if (!longPressTargetRef.current) return;
    const dx = clientX - longPressStartRef.current.x;
    const dy = clientY - longPressStartRef.current.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_THRESHOLD) {
      clearLongPressTimer();
    }
  }, [clearLongPressTimer]);

  const handleLongPressEnd = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  const handleCarouselMouseDown = useCallback((event) => {
    if (event.button !== 0) return;
    handleLongPressStart(event.clientX, event.clientY);
  }, [handleLongPressStart]);

  const handleCarouselMouseMove = useCallback((event) => {
    handleLongPressMove(event.clientX, event.clientY);
  }, [handleLongPressMove]);

  const handleCarouselMouseUp = useCallback(() => {
    handleLongPressEnd();
  }, [handleLongPressEnd]);

  const handleCarouselTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    handleLongPressStart(touch.clientX, touch.clientY);
  }, [handleLongPressStart]);

  const handleCarouselTouchMove = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    handleLongPressMove(touch.clientX, touch.clientY);
  }, [handleLongPressMove]);

  const handleCarouselTouchEnd = useCallback(() => {
    handleLongPressEnd();
  }, [handleLongPressEnd]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  const renderAvatar = (classNameOverride, altText) => {
    const resolvedAvatar = normalizeMediaUrl(avatarUrl, API_CONFIG.BASE_URL);
    if (resolvedAvatar) {
      return <img src={resolvedAvatar} alt={altText} className={classNameOverride} />;
    }
    return <div className={`${classNameOverride} avatar-placeholder`} aria-hidden="true" />;
  };

  const renderSkeletonScreen = () => (
    <div className="iphone loopino-iphone-skeleton" aria-hidden="true">
      <div className="phone-header-container-fixed loopino-iphone-skeleton-statusbar">
        <div className="status-phone-container">
          <div className="time-phone">
            <Skeleton variant="rounded" width="32%" height={52} shimmer />
          </div>
          <div className="loopino-iphone-skeleton-status-icons">
            <Skeleton variant="rounded" width={66} height={34} shimmer />
            <Skeleton variant="rounded" width={66} height={34} shimmer />
            <Skeleton variant="rounded" width={78} height={34} shimmer />
          </div>
        </div>
      </div>

      <div className="instagram-main-container-scroll loopino-iphone-skeleton-scroll">
        <div className="instagram-header">
          <div className="headerr-option-container">
            <div className="header-option loopino-iphone-skeleton-top-row">
              <Skeleton variant="rounded" width="22%" height={44} shimmer />
              <div className="loopino-iphone-skeleton-top-actions">
                <Skeleton variant="circle" width={46} height={46} shimmer />
                <Skeleton variant="circle" width={46} height={46} shimmer />
              </div>
            </div>
          </div>

          <div className="story-slider-container">
            <div className="story-slider loopino-iphone-skeleton-stories">
              {Array.from({ length: 5 }).map((_, index) => (
                <div className="loopino-iphone-skeleton-story" key={`iphone-skel-story-${index}`}>
                  <Skeleton variant="circle" width={120} height={120} shimmer />
                  <Skeleton variant="rounded" width="84%" height={28} shimmer />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="content-container-adaptable">
          <div className="post-info-wrapper">
            <div className="post-info-container loopino-iphone-skeleton-post-header">
              <div className="post-info">
                <div className="medium-profile-img-container">
                  <Skeleton variant="circle" width={90} height={90} shimmer />
                </div>
                <div className="loopino-iphone-skeleton-post-lines">
                  <Skeleton variant="rounded" width="46%" height={38} shimmer />
                  <Skeleton variant="rounded" width="32%" height={30} shimmer />
                </div>
              </div>
              <Skeleton variant="rounded" width={60} height={30} shimmer />
            </div>
          </div>

          <div className="image-carusel-post-resizable-max-proportion-4-3">
            <div className="instagram-carousel-shell">
              <DesktopPostCarousel isLoading />
            </div>
          </div>

          <div className="caurusel-indicatore-wrapper">
            <div className="corusel-indicator loopino-iphone-skeleton-dots" aria-hidden="true">
              <span className="ellipse ellipse-active" />
              <span className="ellipse" />
              <span className="ellipse" />
            </div>
          </div>

          <div className="post-indicaotor-wrapper">
            <div className="group loopino-iphone-skeleton-actions">
              <div className="option-container">
                <div className="option-icon-cont">
                  <Skeleton variant="circle" width={46} height={46} shimmer />
                  <Skeleton variant="rounded" width={84} height={22} shimmer />
                </div>
                <div className="option-icon-cont">
                  <Skeleton variant="circle" width={46} height={46} shimmer />
                  <Skeleton variant="rounded" width={84} height={22} shimmer />
                </div>
                <div className="option-icon-cont">
                  <Skeleton variant="circle" width={46} height={46} shimmer />
                  <Skeleton variant="rounded" width={84} height={22} shimmer />
                </div>
              </div>
              <Skeleton variant="circle" width={46} height={46} shimmer />
            </div>
          </div>

          <div className="info-post-container loopino-iphone-skeleton-caption">
            <Skeleton variant="rounded" width="34%" height={30} shimmer />
            <Skeleton variant="rounded" width="92%" height={28} shimmer />
            <Skeleton variant="rounded" width="70%" height={28} shimmer />
            <Skeleton variant="rounded" width="44%" height={28} shimmer />
            <Skeleton variant="rounded" width="24%" height={24} shimmer />
          </div>
        </div>
      </div>

      <div className="footer-container-fixed loopino-iphone-skeleton-footer">
        <div className="footer-icon-bar loopino-iphone-skeleton-footer-icons">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={`iphone-skel-footer-${index}`} variant="circle" width={46} height={46} shimmer />
          ))}
        </div>
        <div className="footer-phone-bar" />
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={ref}
        className={combinedClassName}
        style={{
          '--iphone-width': `${width}px`,
          '--iphone-height': `${height}px`,
          '--iphone-screen-inset': `${screenInset}px`,
          '--iphone-screen-radius': `${screenRadius}px`,
          '--iphone-content-scale': contentScale,
          '--iphone-content-scale-x': contentScaleX,
          '--iphone-content-scale-y': contentScaleY,
          '--iphone-design-width': `${baseWidth}px`,
          '--iphone-design-height': `${baseHeight}px`,
          '--iphone-island-top': '20px',
          '--iphone-island-width': '110px',
          '--iphone-island-height': '32px'
        }}
        {...props}
      >
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="loopino-iphone-frame"
          aria-hidden="true"
          className="loopino-iphone-frame-graphic"
        >
          <rect
            x="2"
            y="2"
            width={width - 4}
            height={height - 4}
            rx={60}
            fill="none"
            stroke="var(--loopino-iphone-frame-stroke)"
            strokeOpacity="0.4"
            strokeWidth="var(--loopino-iphone-frame-stroke-width)"
          />

          <rect
            x={bezelInset}
            y={bezelInset}
            width={width - bezelInset * 2}
            height={height - bezelInset * 2}
            rx={56}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="1"
          />

        </svg>

        <div className="loopino-iphone-island">
          <span className="loopino-iphone-camera-dot" />
        </div>

        <div
          className="loopino-iphone-screen-mask"
        >
          <div
            className="loopino-iphone-screen-wrapper"
          >
            {isSkeleton ? (
              renderSkeletonScreen()
            ) : (
              <div className="iphone">
                <div className="phone-header-container-fixed">
                  <div className="status-phone-container">
                    <div className="time-phone">
                      <span className="time-value">{deviceTime}</span>
                    </div>
                    <div className="status-phone">
                      <img src={MobileSignalIcon} alt="" aria-hidden="true" className="mobile-signal" />
                      <img src={WifiIcon} alt="" aria-hidden="true" className="wifi" />
                      <img src={BatteryIcon} alt="" aria-hidden="true" className="battery" />
                    </div>
                  </div>
                </div>

                <div className="instagram-main-container-scroll">
                  <div className="instagram-header">
                    <div className="headerr-option-container">
                      <div className="header-option">
                        <div className="foryou-container">
                          <span className="per-te">
                            {t('postReview.preview.forYou', { defaultValue: 'Per te' })}
                          </span>
                          <span className="arrow-down-icon" aria-hidden="true" />
                        </div>
                        <div className="option-container">
                          <img src={LikeIcon} alt="" aria-hidden="true" className="like" />
                          <img src={MessengerIcon} alt="" aria-hidden="true" className="messanger" />
                        </div>
                      </div>
                    </div>

                    <div className="story-slider-container">
                      <div className="story-slider">
                        <div className="personal-profile-container">
                          <div className="personal-profile-img-container">
                            <div className="profile-big-outside profile-big-outside--personal">
                              {renderAvatar('profile-big-inside', t('postReview.preview.yourStory', { defaultValue: 'La tua storia' }))}
                            </div>
                            <div className="icon-add-story">
                              <img src={AddStoryIcon} alt="" aria-hidden="true" />
                            </div>
                          </div>
                          <div className="profile-name">
                            <span className="la-tua-storia">
                              {t('postReview.preview.yourStory', { defaultValue: 'La tua storia' })}
                            </span>
                          </div>
                        </div>

                        {placeholderStories.map((story) => (
                          <div className="profile-container" key={story.name}>
                            <div className="profile-img-container">
                              <div className="profile-big-outside">
                                <img src={story.avatar} alt={story.name} className="profile-big-inside" />
                              </div>
                            </div>
                            <div className="profile-name">
                              <span className="altro-user">{story.name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="content-container-adaptable">
                    <div className="post-info-wrapper">
                      <div className="post-info-container">
	                  <div className="post-info">
	                          <div className="medium-profile-img-container">
	                            <div className="profile-medium-inside">
	                              {renderAvatar('profile-medium-img', resolvedProfileName)}
	                            </div>
	                          </div>
	                          <div className="post-name">
	                            <span className="nomeprofilopersonale">{resolvedProfileName}</span>
	                            <span className="luogo">{resolvedProfileLocation}</span>
	                          </div>
	                        </div>
                        <div className="more-icon" aria-hidden="true">
                          <span className="moreicon" />
                        </div>
                      </div>
                    </div>

                    <div className="image-carusel-post-resizable-max-proportion-4-3">
                      <div
                        className="instagram-carousel-shell"
                        onMouseDown={handleCarouselMouseDown}
                        onMouseUp={handleCarouselMouseUp}
                        onMouseLeave={handleCarouselMouseUp}
                        onMouseMove={handleCarouselMouseMove}
                        onTouchStart={handleCarouselTouchStart}
                        onTouchEnd={handleCarouselTouchEnd}
                        onTouchCancel={handleCarouselTouchEnd}
                        onTouchMove={handleCarouselTouchMove}
                      >
                        <DesktopPostCarousel
                          images={normalizedMedia}
                          currentIndex={currentImageIndex}
                          onIndexChange={handleIndexChange}
                          onRegenerateImage={onRegenerateImage}
                          onUploadImage={onUploadImage}
                          isRegenerating={isRegenerating}
                          disabled={isProcessing || isCurrentPostPublished || isRegenerating}
                          isPostActive
                          getImageLoadingProps={getImageLoadingProps}
                          imageLoadingScope="preview-carousel"
                        />
                      </div>
                    </div>

                    <div className="caurusel-indicatore-wrapper">
                      <div className="corusel-indicator">
                        {normalizedMedia.length > 0 ? normalizedMedia.map((_, idx) => (
                          <span
                            key={idx}
                            className={['ellipse', idx === currentImageIndex ? 'ellipse-active' : ''].filter(Boolean).join(' ')}
                            aria-label={
                              idx === currentImageIndex
                                ? t('postReview.preview.media.active', { defaultValue: 'Media attivo' })
                                : t('postReview.preview.media.item', { defaultValue: 'Media' })
                            }
                          />
                        )) : (
                          <>
                            <span className="ellipse ellipse-active" />
                            <span className="ellipse" />
                            <span className="ellipse" />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="post-indicaotor-wrapper">
                        <div className="group">
                          <div className="option-container">
                            <div className="option-icon-cont">
                            <img src={LikeIcon} alt="" aria-hidden="true" className="post-action-icon" />
                            <span className="count-label">{counts.likes.toLocaleString(language === 'en' ? 'en-US' : 'it-IT')}</span>
                          </div>
                          <div className="option-icon-cont">
                            <img src={CommentIcon} alt="" aria-hidden="true" className="post-action-icon" />
                            <span className="count-label">{counts.comments.toLocaleString(language === 'en' ? 'en-US' : 'it-IT')}</span>
                          </div>
                          <div className="option-icon-cont">
                            <img src={MessengerIcon} alt="" aria-hidden="true" className="post-action-icon" />
                            <span className="count-label">{counts.shares.toLocaleString(language === 'en' ? 'en-US' : 'it-IT')}</span>
                          </div>
                        </div>
                        <img src={SaveIcon} alt={t('common.actions.save', { defaultValue: 'Salva' })} className="save" />
                      </div>
                    </div>

	                    <div className="info-post-container">
	                      <div className="board">
	                        <div className="post-title-line">
	                          <span className="nomeprofilopersonale">{resolvedProfileName}</span>
	                          <span className="descrizione-post">{resolvedCaptionText}</span>
	                        </div>
	                        {hashtagsLine ? <span className="descrizione-post hashtags">{hashtagsLine}</span> : null}
	                        <span className="comments-hint">{resolvedCommentsHint}</span>
	                      </div>
	                      <span className="data-post">{resolvedRelativeTimeLabel}</span>
	                    </div>
                  </div>
                </div>

                <div className="footer-container-fixed">
                  <div className="footer-icon-bar">
                    <img src={HomeIcon} alt="" aria-hidden="true" className="home-icon" />
                    <img src={SearchIcon} alt="" aria-hidden="true" className="search-icon" />
                    <img src={AddIcon} alt="" aria-hidden="true" className="add-icon" />
	                    <img src={CameraIcon} alt="" aria-hidden="true" className="add-icon" />
	                    <div className="small-profile-img-container">
	                      {renderAvatar('profile-small-inside', resolvedProfileName)}
	                    </div>
	                  </div>
	                  <div className="footer-phone-bar" />
	                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(viewerUrl || isViewerVisible) && (
        <div
          className={`loopino-desktop-gallery-viewer ${isViewerVisible ? 'is-visible' : ''}`}
          onClick={closeViewer}
          role="dialog"
          aria-modal="true"
        >
          <div className="loopino-desktop-gallery-viewer-card" onClick={(event) => event.stopPropagation()}>
            <div className="loopino-desktop-gallery-viewer-header">
              <div>
                <p className="loopino-desktop-gallery-eyebrow">
                  {t('imageGallery.viewer.defaultTitle', { defaultValue: 'Anteprima immagine' })}
                </p>
                <h4>{t('imageGallery.viewer.defaultTitle', { defaultValue: 'Anteprima immagine' })}</h4>
              </div>
              <div className="loopino-desktop-gallery-viewer-actions">
                <button
                  type="button"
                  className="loopino-button"
                  data-variant="ghost"
                  onClick={handleViewerDownload}
                  aria-label={t('imageGallery.viewer.downloadButton', { defaultValue: 'Scarica' })}
                  disabled={!viewerUrl}
                >
                  <img src={iconDownload} alt="" aria-hidden="true" className="loopino-desktop-gallery-action-icon" />
                </button>
                <button
                  type="button"
                  className="loopino-button"
                  data-variant="ghost"
                  onClick={closeViewer}
                  aria-label={t('imageGallery.viewer.closeButton', { defaultValue: 'Chiudi' })}
                >
                  <img src={iconClose} alt="" aria-hidden="true" className="loopino-desktop-gallery-action-icon" />
                </button>
              </div>
            </div>
            <div className="loopino-desktop-gallery-viewer-body">
              <img src={viewerUrl || ''} alt={t('imageGallery.viewer.defaultTitle', { defaultValue: 'Anteprima immagine' })} />
            </div>
          </div>
        </div>
      )}
    </>
  );
});

Iphone16Pro.displayName = 'Iphone16Pro';

export default Iphone16Pro;
export { Iphone16Pro };
